import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useFormContext } from 'react-hook-form';
import TwSelect from 'react-tailwindcss-select';

import { useInsideDialog } from '@/components/Ui/Dialog';
import { useElementRect } from '@/hooks/useElementRect';

interface Option {
    value: string;
    label: string;
}

interface OptionGroup {
    label: string;
    options: Option[];
}

interface Props {
    name: string;
    options: Array<Option | OptionGroup>;
    placeholder?: string;
    isDisabled?: boolean;
    isMultiple?: boolean;
    isClearable?: boolean;
    isSearchable?: boolean;
    className?: string;
    onChange?: (value: string | string[] | null) => void;

    /**
     * Render the dropdown menu into a `document.body` portal, anchored to
     * the trigger's live position — the same approach `TextAutocomplete`
     * uses (an input doubling as trigger/search box, with only the
     * suggestion/option list portaled and positioned via `useElementRect`,
     * recomputed only while open). Needed for fields inside a `Dialog`,
     * whose `overflow-hidden`/`overflow-y-auto` ancestors otherwise clip
     * the menu. Defaults to automatic: on inside a `Dialog` (detected via
     * context), off on regular pages, where that clipping doesn't happen.
     */
    usePortal?: boolean;
}

/**
 * Strip diacritics and lowercase, so "Fe" matches "Félice". The library's
 * own built-in search only lowercases — it doesn't fold accents.
 */
function normalize(value: string): string {
    // Strips NFD combining diacritical marks (U+0300–U+036F).
    return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function filterOptions(options: Array<Option | OptionGroup>, query: string): Array<Option | OptionGroup> {
    if (!query) {
        return options;
    }

    const normalizedQuery = normalize(query);

    return options.reduce<Array<Option | OptionGroup>>((acc, option) => {
        if ('value' in option) {
            if (normalize(option.label).includes(normalizedQuery)) {
                acc.push(option);
            }

            return acc;
        }

        const matchingGroupOptions = option.options.filter((o) => {
            return normalize(o.label).includes(normalizedQuery);
        });

        if (matchingGroupOptions.length > 0) {
            acc.push({ ...option, options: matchingGroupOptions });
        }

        return acc;
    }, []);
}

function flattenOptions(options: Array<Option | OptionGroup>): Option[] {
    return options.flatMap((o) => {
        return 'value' in o ? [
            o,
        ] : o.options;
    });
}

interface PortalSelectProps {
    options: Array<Option | OptionGroup>;
    placeholder: string;
    isDisabled?: boolean;
    isMultiple?: boolean;
    isClearable?: boolean;
    isSearchable?: boolean;
    className: string;
    matches: Option[];
    onSelect: (option: Option) => void;
    onClear: () => void;
    onBlurField?: () => void;

    /**
     * Server-driven search: called with the raw search text on every
     * keystroke instead of filtering `options` client-side (the caller is
     * expected to refresh `options`, e.g. via a debounced request).
     */
    onSearchTextChange?: (text: string) => void;
    noOptionsMessage?: string;
}

/**
 * Portal-anchored replacement for `TwSelect`'s trigger+menu, used when
 * `usePortal` is set. An earlier attempt portaled the whole trigger+menu
 * pair, which then rendered behind the dialog's own `document.body` portal
 * and effectively vanished. This mirrors `TextAutocomplete` instead: the
 * trigger stays in normal flow (so it can't be mis-stacked), and only the
 * option list is portaled, anchored via `useElementRect` which only
 * recomputes while the menu is open.
 */
export function PortalSelect({
    options,
    placeholder,
    isDisabled,
    isMultiple,
    isClearable,
    isSearchable,
    className,
    matches,
    onSelect,
    onClear,
    onBlurField,
    onSearchTextChange,
    noOptionsMessage,
}: PortalSelectProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [
        isOpen,
        setIsOpen,
    ] = useState(false);
    const [
        searchText,
        setSearchText,
    ] = useState('');
    const rect = useElementRect(inputRef, isOpen);
    const disabledClasses = isDisabled ? 'opacity-50' : '';
    const selectedLabel = matches.map((m) => {
        return m.label;
    }).join(', ');
    const displayValue = isOpen && isSearchable ? searchText : selectedLabel;
    const visibleOptions = isSearchable && !onSearchTextChange ? filterOptions(options, searchText) : options;

    const pick = (option: Option) => {
        onSelect(option);
        setSearchText('');

        if (!isMultiple) {
            setIsOpen(false);
            inputRef.current?.blur();
        }
    };

    const renderOption = (option: Option) => {
        const isSelected = matches.some((m) => {
            return m.value === option.value;
        });

        return (
            <li key={ option.value }>
                <button
                    className={ `w-full text-left px-3 py-1.5 hover:bg-muted ${ isSelected ? 'bg-muted' : '' }` }
                    onClick={ () => {
                        pick(option);
                    } }
                    onMouseDown={ (e) => {
                        e.preventDefault();
                    } }
                    type='button'
                >
                    { option.label }
                </button>
            </li>
        );
    };

    return (
        <div className='relative grow'>
            <input
                className={ `w-full px-3 py-2 rounded-md border border-input bg-background text-sm text-muted-foreground shadow-sm transition-all duration-300 focus:outline-none hover:border-ring focus:border-info focus:ring focus:ring-info/20 disabled:cursor-not-allowed ${ isSearchable ? '' : 'cursor-pointer caret-transparent' } ${ className } ${ disabledClasses }` }
                disabled={ isDisabled }
                onBlur={ () => {
                    onBlurField?.();
                    setIsOpen(false);
                } }
                onChange={ isSearchable ? (e) => {
                    setSearchText(e.target.value);
                    onSearchTextChange?.(e.target.value);
                    setIsOpen(true);
                } : undefined }
                onFocus={ () => {
                    setSearchText('');
                    setIsOpen(true);
                } }
                placeholder={ placeholder }
                readOnly={ !isSearchable }
                ref={ inputRef }
                value={ displayValue }
            />
            { isClearable && matches.length > 0 && (
                <button
                    className='absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-destructive'
                    onClick={ onClear }
                    onMouseDown={ (e) => {
                        e.preventDefault();
                    } }
                    type='button'
                >
                    ×
                </button>
            ) }
            { isOpen && rect && createPortal(
                <ul
                    className='fixed z-[60] bg-background shadow-lg border border-border rounded py-1 text-sm text-foreground max-h-72 overflow-y-auto'
                    style={ { top: rect.top + rect.height + 6, left: rect.left, width: rect.width } }
                >
                    { visibleOptions.length === 0 && (
                        <li className='px-3 py-1.5 text-muted-foreground'>{ noOptionsMessage ?? 'No options' }</li>
                    ) }
                    { visibleOptions.map((o) => {
                        if ('value' in o) {
                            return renderOption(o);
                        }

                        return (
                            <li key={ o.label }>
                                <div className='px-3 py-1 text-xs font-semibold text-muted-foreground'>{ o.label }</div>
                                <ul>
                                    { o.options.map((opt) => {
                                        return renderOption(opt);
                                    }) }
                                </ul>
                            </li>
                        );
                    }) }
                </ul>,
                document.body
            ) }
        </div>
    );
}

/**
 * RHF-compatible analog of `Ui/Form/Select.jsx` (the non-iOS branch of
 * `AdaptiveSelect`). Wraps `react-tailwindcss-select` and bridges the
 * option-object ↔ scalar-value gap via `<Controller>`.
 */
export function Select({
    name,
    options,
    placeholder = 'Select…',
    isDisabled,
    isMultiple,
    isClearable,
    isSearchable,
    className = '',
    onChange,
    usePortal,
}: Props) {
    const { control } = useFormContext();
    const insideDialog = useInsideDialog();
    const shouldPortal = usePortal ?? insideDialog;
    const disabledClasses = isDisabled ? 'opacity-50' : '';
    const [
        searchText,
        setSearchText,
    ] = useState('');

    return (
        <Controller
            control={ control }
            name={ name }
            render={ ({ field }) => {
                const flat = flattenOptions(options);

                const matches = flat.filter((o) => {
                    // Coerce both sides to string so numeric ids loaded from the
                    // API still match the string option values emitted by
                    // AjaxSelect's String(valueFn(...)) boundary.
                    if (Array.isArray(field.value)) {
                        return (field.value as Array<string | number>).some((v) => {
                            return String(v) === o.value;
                        });
                    }

                    if (field.value === null || field.value === undefined) {
                        return false;
                    }

                    return String(field.value) === o.value;
                });

                if (shouldPortal) {
                    return (
                        <PortalSelect
                            className={ className }
                            isClearable={ isClearable }
                            isDisabled={ isDisabled }
                            isMultiple={ isMultiple }
                            isSearchable={ isSearchable }
                            matches={ matches }
                            onBlurField={ field.onBlur }
                            onClear={ () => {
                                const empty = isMultiple ? [] : null;

                                field.onChange(empty);
                                onChange?.(empty);
                            } }
                            onSelect={ (option) => {
                                if (isMultiple) {
                                    const currentValues = Array.isArray(field.value) ? field.value as string[] : [];
                                    const nextValues = currentValues.includes(option.value)
                                        ? currentValues.filter((v) => {
                                            return v !== option.value;
                                        })
                                        : [
                                            ...currentValues,
                                            option.value,
                                        ];

                                    field.onChange(nextValues);
                                    onChange?.(nextValues);
                                    return;
                                }

                                field.onChange(option.value);
                                onChange?.(option.value);
                            } }
                            options={ options }
                            placeholder={ placeholder }
                        />
                    );
                }

                let value;

                if (matches.length === 0) {
                    value = null;
                } else if (isMultiple) {
                    value = matches;
                } else {
                    value = matches[0];
                }

                return (
                    <div className='grow'>
                        <TwSelect
                            classNames={ {
                                menuButton: () => {
                                    return `w-full rounded-md flex text-sm text-muted-foreground border border-input rounded-md shadow-sm transition-all duration-300 focus:outline-none hover:border-ring focus:border-info focus:ring focus:ring-info/20 bg-background ${ className } ${ disabledClasses }`;
                                },
                                list: 'bg-background max-h-72 overflow-y-auto',
                                menu: 'z-10 absolute w-full bg-background shadow-lg border border-border rounded py-1 mt-1.5 text-sm text-foreground',
                                tagItem: (args) => {
                                    return `relative flex items-center rounded-md text-xs font-medium bg-muted text-foreground ${ args?.isDisabled ? 'opacity-50' : '' }`;
                                },
                                tagItemText: 'pl-2 pr-6 py-1 pointer-events-none',
                                tagItemIconContainer: 'absolute inset-0 flex items-center justify-end pr-1.5 cursor-pointer rounded-md hover:bg-destructive/10 hover:text-destructive dark:hover:bg-destructive/20 dark:hover:text-destructive',
                            } }
                            isClearable={ isClearable }
                            isDisabled={ isDisabled }
                            isMultiple={ isMultiple }
                            isSearchable={ isSearchable }
                            onChange={ (val) => {
                                setSearchText('');
                                if (!val) {
                                    const empty = isMultiple ? [] : null;

                                    field.onChange(empty);
                                    onChange?.(empty);
                                    return;
                                }
                                if (Array.isArray(val)) {
                                    const values = val.map((o) => {
                                        return o.value;
                                    });

                                    field.onChange(values);
                                    onChange?.(values);
                                } else {
                                    field.onChange(val.value);
                                    onChange?.(val.value);
                                }
                            } }
                            onSearchInputChange={ isSearchable ? (e) => {
                                setSearchText((e.target as HTMLInputElement).value);
                            } : undefined }
                            options={ isSearchable ? filterOptions(options, searchText) : options }
                            placeholder={ placeholder }
                            primaryColor=''
                            value={ value }
                        />
                    </div>
                );
            } }
        />
    );
}
